/**
 * Builds the `TerminalConfig` that wires `@tradeparadigm/terminal-react` into
 * Paradigm: Redux auth for identity, the server-synced preferences blob for
 * cross-device gateway URL/pairing token, desk-ticker URL derivation as the
 * last-resort default, and the block-trade preamble for prompt framing.
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getUserId } from '@paradigm/store/src/auth/selectors';
import { logException } from '@paradigm/logging/src/logging';
import type { TerminalConfig } from '@tradeparadigm/terminal-react';

import { PARADIGM_BLOCK_TRADE_PREAMBLE } from './block-trade-preamble';
import { PARADIGM_TERMINAL_NAMESPACE } from './agent-url';

/** Client-id prefix shown in the gateway's pairing-approval prompt. */
export const PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX =
  'paradigm-block-terminal';

/**
 * Terminal config for the signed-in Paradigm user. With no user (or during
 * the logged-out boundary) `accountId` is null, which the component treats
 * as signed out: no auto-connect, no storage reads.
 *
 * The terminal owns pairing end-to-end: it shows its own login screen and
 * persists the gateway URL + token in its own (per-device) browser storage.
 * This host injects no URL/token — hence no `resolveUrl` / `persistAgentUrl` /
 * `fallbackPairingToken`. If Paradigm later gains managed sign-in (fetching
 * credentials server-side), push them in via `useTerminalAuth().login(...)`
 * from an `agentEffects` node rather than reviving the preferences bridge.
 */
export function useParadigmTerminalConfig(): TerminalConfig {
  const userId = useSelector(getUserId);

  // The auth reducer resets `user.id` to `0` (not `null`) on logout, so
  // treat the falsy / "empty user" case as signed out.
  const accountId =
    userId != null && userId !== 0 && String(userId).length > 0
      ? String(userId)
      : null;

  return useMemo<TerminalConfig>(
    () => ({
      accountId,
      namespace: PARADIGM_TERMINAL_NAMESPACE,

      clientIdPrefix: PARADIGM_BLOCK_TERMINAL_CLIENT_ID_PREFIX,
      // Desktop's historical connection posture: bounded reconnect attempts
      // via circuit breaker (vs Paradex's heartbeat), and no pairing-role
      // fallback — Paradigm gateways pair via a token, entered in the
      // terminal's own login screen and stored per-device.
      healthStrategy: 'circuit-breaker',
      supportsPairingRole: false,

      systemPreamble: PARADIGM_BLOCK_TRADE_PREAMBLE,

      logException: (error: unknown) => logException('dime-terminal', error),

      features: { cron: true },
    }),
    [accountId],
  );
}
